import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, CheckCircle, Clock, RotateCcw } from "lucide-react";
import { Layout } from "@/components/Layout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { useAuditReports, type AuditStatusFilter } from "@/hooks/api/useAudit";
import type { AiFinding } from "@arna-med/shared";

const STATUS_FILTERS: { key: AuditStatusFilter; labelKey: string }[] = [
  { key: "pending_audit", labelKey: "audit.filterPending" },
  { key: "revision_requested", labelKey: "audit.filterRevision" },
  { key: "approved", labelKey: "audit.filterApproved" },
  { key: "all", labelKey: "audit.filterAll" },
];

function StatusBadge({ status }: { status: string }) {
  const { t } = useTranslation();
  const map: Record<string, { label: string; className: string; icon: React.ReactNode }> = {
    pending_audit: {
      label: t("audit.pendingAudit"),
      className: "bg-yellow-100 text-yellow-800",
      icon: <Clock className="w-3 h-3" />,
    },
    approved: {
      label: t("audit.approved"),
      className: "bg-green-100 text-green-800",
      icon: <CheckCircle className="w-3 h-3" />,
    },
    revision_requested: {
      label: t("audit.revisionRequested"),
      className: "bg-red-100 text-red-800",
      icon: <RotateCcw className="w-3 h-3" />,
    },
    not_submitted: {
      label: t("audit.notSubmitted"),
      className: "bg-gray-100 text-gray-600",
      icon: null,
    },
  };
  const cfg = map[status] ?? map.not_submitted;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.className}`}>
      {cfg.icon}
      {cfg.label}
    </span>
  );
}

function AiFlagsCell({ findings }: { findings: AiFinding[] | null | undefined }) {
  const { t } = useTranslation();
  if (!findings?.length) return <span className="text-muted-foreground text-sm">—</span>;
  const errors = findings.filter((f) => !f.fixed && f.severity === "error").length;
  const warnings = findings.filter((f) => !f.fixed && f.severity === "warning").length;
  const fixed = findings.filter((f) => f.fixed).length;
  return (
    <div className="flex flex-wrap gap-1">
      {errors > 0 && (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-800">
          <AlertTriangle className="w-3 h-3" />
          {errors}
        </span>
      )}
      {warnings > 0 && (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-yellow-100 text-yellow-800">
          <AlertTriangle className="w-3 h-3" />
          {warnings}
        </span>
      )}
      {fixed > 0 && (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-800">
          <CheckCircle className="w-3 h-3" />
          {fixed} {t("audit.aiFixed")}
        </span>
      )}
    </div>
  );
}

export default function AuditDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState<AuditStatusFilter>("pending_audit");
  const { data, isLoading } = useAuditReports(activeFilter);

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        <h1 className="text-2xl font-semibold">{t("audit.dashboard")}</h1>

        {/* Filter tabs */}
        <div className="flex gap-2 flex-wrap">
          {STATUS_FILTERS.map(({ key, labelKey }) => (
            <Button
              key={key}
              variant={activeFilter === key ? "default" : "outline"}
              size="sm"
              onClick={() => setActiveFilter(key)}
            >
              {t(labelKey)}
            </Button>
          ))}
        </div>

        {isLoading ? (
          <div className="flex justify-center py-12"><Spinner /></div>
        ) : !data?.length ? (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              {t("audit.noReports")}
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left px-4 py-3 font-medium">{t("audit.reportsTable.date")}</th>
                    <th className="text-left px-4 py-3 font-medium">{t("audit.reportsTable.patient")}</th>
                    <th className="text-left px-4 py-3 font-medium">{t("audit.reportsTable.doctor")}</th>
                    <th className="text-left px-4 py-3 font-medium">{t("audit.reportsTable.aiFlags")}</th>
                    <th className="text-left px-4 py-3 font-medium">{t("audit.reportsTable.status")}</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {data.map(({ report, consultation, doctor }) => (
                    <tr
                      key={report.id}
                      className="border-b last:border-0 hover:bg-muted/30 cursor-pointer transition-colors"
                      onClick={() => navigate(`/auditor/report/${report.id}`)}
                    >
                      <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">
                        {new Date(consultation.startedAt).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 font-medium">{consultation.patientName}</td>
                      <td className="px-4 py-3 text-muted-foreground">{doctor.name}</td>
                      <td className="px-4 py-3">
                        <AiFlagsCell findings={report.aiFindings as AiFinding[] | null} />
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={report.auditStatus ?? "not_submitted"} />
                      </td>
                      <td className="px-4 py-3">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/auditor/report/${report.id}`);
                          }}
                        >
                          {t("common.open")}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
